import { useEffect, useRef } from "react";

const BAR_COUNT = 64;
const BAR_GAP = 2;
const BAR_WIDTH = 3;

export function RecordingWaveform({
	analyser,
	active,
	height = 56,
}: {
	analyser: AnalyserNode | null;
	active: boolean;
	height?: number;
}) {
	const svgRef = useRef<SVGSVGElement>(null);
	const rafRef = useRef<number | null>(null);

	useEffect(() => {
		if (!analyser || !active) return;
		const data = new Uint8Array(analyser.frequencyBinCount);

		function draw() {
			analyser?.getByteFrequencyData(data);
			const svg = svgRef.current;
			if (!svg) {
				rafRef.current = requestAnimationFrame(draw);
				return;
			}
			const bars = svg.querySelectorAll<SVGRectElement>("rect");
			const stride = Math.floor(data.length / BAR_COUNT);
			bars.forEach((bar, i) => {
				const v = data[i * stride] ?? 0;
				const norm = v / 255;
				const h = Math.max(2, norm * (height - 4));
				bar.setAttribute("height", String(h));
				bar.setAttribute("y", String((height - h) / 2));
			});
			rafRef.current = requestAnimationFrame(draw);
		}
		draw();
		return () => {
			if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
		};
	}, [analyser, active, height]);

	const totalWidth = BAR_COUNT * (BAR_WIDTH + BAR_GAP);
	return (
		<svg
			ref={svgRef}
			viewBox={`0 0 ${totalWidth} ${height}`}
			preserveAspectRatio="none"
			className="w-full"
			style={{ height }}
			aria-hidden="true"
		>
			{Array.from({ length: BAR_COUNT }).map((_, i) => (
				<rect
					// biome-ignore lint/suspicious/noArrayIndexKey: fixed-length bars
					key={i}
					x={i * (BAR_WIDTH + BAR_GAP) + 1}
					y={height / 2 - 1}
					width={BAR_WIDTH}
					height={2}
					rx={1}
					className="fill-primary"
					opacity={active ? 1 : 0.4}
				/>
			))}
		</svg>
	);
}
